import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Table,
  Button as AntButton,
  Space,
  Dropdown,
  Drawer,
  Form,
  Input,
  Upload,
  Row,
  Col,
} from "antd";
import { Button, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import {
  UploadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import { documentApis } from "../../../api/Documents";
import { useAccessibility, canAdd } from "../../../utils/accessControl";
import { CancelAccess } from "../../../components/accessControl/AccessControl";
import SearchInput from "../../form-components/SearchComponent";
import { SearchOutlined } from "@ant-design/icons";
import { FormProvider, useForm } from "react-hook-form";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";

const { TextArea } = Input;
// Link type options - context aware

interface DocumentsTabProps {
  linkType: "project" | "phase" | "activity" | "task" | "issue";
  linkToId: string | number;
  linkToName: string;
  projectId?: number; // Required for phase documents
  projectName?: string; // Project name from context
  phaseName?: string; // Phase name from context
  activityName?: string; // Activity name from context
  taskName?: string; // Task name from context
  issueName?: string; // Issue name from context
  shouldLoadData?: boolean; // Control when to load data
  isFromWorkUI?: boolean; // Flag to indicate if this is from Work UI
  openDrawer?: boolean; // External control to open drawer
  onDrawerClose?: () => void; // Callback when drawer closes
  onDocumentsSubmit?: (data: { docKeys: any[]; files: File[] }) => void; // Callback when documents are submitted (for Task/Issue creation)
  initialDocData?: { docKeys: any[]; files: File[] }; // Initial document data to restore when reopening drawer
  parentDrawerMode?: boolean; // When true, component is inside another drawer - uses push mode for nested drawer
}

interface DocumentData {
  paginationId: number;
  docId: number;
  docName: string;
  customDocName: string;
  linkType: string;
  linkTo: string;
  remarks: string;
  filePath: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  linkType,
  linkToId,
  linkToName,
  projectId,
  projectName,
  phaseName,
  activityName,
  taskName,
  issueName,
  shouldLoadData = true, // Default to true for backward compatibility
  isFromWorkUI = false,
  openDrawer = false,
  onDrawerClose,
  onDocumentsSubmit,
  initialDocData,
  parentDrawerMode = false, // When inside another drawer, uses push mode
}) => {
  const methods = useForm();

  // Add minimal CSS for hover effects and form styling
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .file-row-hover:hover .delete-icon,
      .file-row-hover:hover .download-icon {
        opacity: 1 !important;
        transform: scale(1.1) !important;
      }
      .delete-icon, .download-icon {
        opacity: 0.3 !important;
        transition: all 0.2s ease !important;
      }
      .delete-icon { color: #ff4d4f !important; }
      .download-icon { color: #52c41a !important; }
      .ant-upload, .ant-upload .ant-btn, .ant-form-item-control-input, .ant-input {
        width: 100% !important;
      }
      .ant-upload-list-item-actions .anticon-delete,
      .ant-upload-list-item-actions .ant-btn {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .ant-table-wrapper {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: auto !important;
      }
      .ant-table {
        width: 100% !important;
        max-width: 100% !important;
      }
      .ant-table-container {
        width: 100% !important;
        max-width: 100% !important;
      }
      .ant-input[disabled] {
        background-color: #f5f5f5 !important;
        color: #262626 !important;
        cursor: not-allowed !important;
        border-color: #d9d9d9 !important;
      }
      
      /* Disabled Select components styling to match Input styling */
      .ant-select-disabled .ant-select-selector {
        background-color: #f5f5f5 !important;
        color: #262626 !important;
        cursor: not-allowed !important;
        border-color: #d9d9d9 !important;
      }
      
      .ant-select-disabled .ant-select-selection-item {
        color: #262626 !important;
      }
      
      /* Simple disabled document name styling */
      .disabled-doc-name.ant-input[disabled] {
        background-color: #f5f6fa !important;
        color: #3c4d62 !important;
      }
      
      .disabled-doc-name.ant-input[disabled] input {
        background-color: #f5f6fa !important;
        color: #3c4d62 !important;
      }

    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const location = useLocation();
  const [isModalVisible, setIsModalVisible] = useState(openDrawer || false);
  const [form] = Form.useForm();
  const [documentCount, setDocumentCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DocumentData[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });

  const [projectList, setProjectList] = useState<any[]>([]);
  const [projectNameMapping, setProjectNameMapping] = useState<{
    [key: string]: string;
  }>({});

  const [selectedLinkTypes, setSelectedLinkTypes] = useState<string[]>([]);
  const [selectedProjectForPhase, setSelectedProjectForPhase] = useState<
    number | null
  >(null);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddingToGroup, setIsAddingToGroup] = useState(false);

  // Add accessibility hook for access control
  const accessibility = useAccessibility();

  // Function to handle screen size changes
  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  // Function to handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Reset pagination when searching
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    // Fetch data with new search query
    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: 10,
      searchQuery: value,
    });
  };

  // Function to download document
  const downloadDocument = (filePath: string, fileName: string) => {
    try {
      // Force download by using fetch and blob
      fetch(filePath)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "document";
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          // Fallback to direct download
          const link = document.createElement("a");
          link.href = filePath;
          link.download = fileName || "document";
          link.target = "_blank";
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    } catch (error) {
      // Final fallback - open in new tab
      window.open(filePath, "_blank");
    }
  };

  // Delete all files function (for grouped documents)
  const handleDeleteAllFiles = (record: any) => {
    const fileCount = record.documents.length;

    Swal.fire({
      title: "Delete All Files?",
      text: `This will delete all ${fileCount} files in this document record. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete all files!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for deletion
          const formDataToSend = new FormData();

          // Create payload for deletion - delete all files by sending all file indices
          const deletedFileData = record.documents.map(
            (doc: any, index: number) => ({
              docId: doc.docId,
              fileIndex: index,
            })
          );

          const filesData = {
            newfiledata: [],
            deletedFileData: deletedFileData,
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("Delete All Files API call failed:", apiError);
            Swal.fire({
              icon: "error",
              title: "API Error",
              text: "Delete All Files API call failed. Please check the console for details.",
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `All ${fileCount} files have been deleted successfully.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("Delete All Files failed:", response);
            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: `Delete All Files failed: ${
                response?.error || response?.message || "Please try again."
              }`,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("Delete All Files error:", error);
          Swal.fire("Error", "Failed to delete all files.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Function to group documents by linkType, linkTo, and document name
  const groupDocumentsByLink = (documents: any[]) => {
    const groupedMap = new Map();

    documents.forEach((doc) => {
      // Create group key based on linkType, linkTo, and document name
      const docName = doc.customDocName?.trim() || doc.docName?.trim() || "";
      const groupKey = `${doc.linkType}_${doc.linkTo}_${docName}`;

      if (groupedMap.has(groupKey)) {
        // If group exists, add this document to the group
        const existingGroup = groupedMap.get(groupKey);
        existingGroup.documents.push(doc);
        existingGroup.docCount = existingGroup.documents.length;

        // Show the document name (since all documents in group have same name)
        existingGroup.docNames = docName;
        existingGroup.customDocName = docName;

        // Preserve existing remarks if new document doesn't have remarks
        if (!doc.remarks && existingGroup.remarks) {
          // Keep the existing remarks from the group
        } else if (doc.remarks && !existingGroup.remarks) {
          // If new document has remarks but group doesn't, update group remarks
          existingGroup.remarks = doc.remarks;
        }
        // If both have remarks, keep the existing group remarks (first document's remarks)
      } else {
        // Create new group
        groupedMap.set(groupKey, {
          ...doc,
          documents: [doc],
          docCount: 1,
          docNames: docName,
          customDocName: docName,
          key: groupKey,
        });
      }
    });

    const result = Array.from(groupedMap.values());
    return result;
  };

  // Fetch documents for the specific link type and ID
  const fetchData = async (params: any) => {
    setLoading(true);
    try {
      const {
        pageSize = 10,
        searchQuery = "",
        sortOrder = "DESC",
        direction = "P",
        pageDiff = 0,
        paginationId = 10,
      } = params;

      // Build request payload with ID fields based on linkType
      const requestPayload: any = {
        modules: linkType,
        sortOrder,
        direction,
        pageSize,
        pageDiff,
        paginationId,
        searchQuery: searchQuery, // Use the search state
      };

      // Add ID fields based on linkType
      if (linkType === "project") {
        requestPayload.projectIds = linkToId.toString();
      } else if (linkType === "phase") {
        requestPayload.phaseIds = linkToId.toString();
      } else if (linkType === "activity") {
        requestPayload.activityIds = linkToId.toString();
      } else if (linkType === "task") {
        requestPayload.taskIds = linkToId.toString();
      } else if (linkType === "issue") {
        requestPayload.issueIds = linkToId.toString();
      }

      const apiResponse: any = await documentApis.listDocuments(requestPayload);

      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (apiResponse.list !== undefined) {
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData) {
        const { list, count } = responseData || {};

        if (list && Array.isArray(list)) {
          setPagination((prev) => ({
            ...prev,
            total: Number(count) || 0,
          }));

          // Group documents by linkType and linkTo if needed
          const groupedData = groupDocumentsByLink(list);

          // Add key to each item for Ant Design table
          const dataWithKeys = groupedData.map((item: any, index: number) => ({
            ...item,
            key: item.docId || index,
            // Ensure customDocName is properly set for display
            customDocName: item.customDocName || item.docName || item.docNames,
          }));

          setDataSource(dataWithKeys);
        } else {
          setDataSource([]);
        }
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get Link To options based on Link Type and Context

  // Fetch project list
  const fetchProjectList = async () => {
    try {
      const requestPayload = {
        projectStatus: "Active", // You might want to make this configurable
      };

      const apiResponse: any = await documentApis.projectList(requestPayload);

      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setProjectList(responseData);

        const projectMapping: { [key: string]: string } = {};
        responseData.forEach((project: any) => {
          projectMapping[project.projectId.toString()] = project.projectTitle;
        });
        setProjectNameMapping(projectMapping);
      } else {
        setProjectList([]);
        setProjectNameMapping({});
      }
    } catch (error) {
      console.error("Error loading project list:", error);
      setProjectList([]);
    }
  };

  // Fetch task list for task documents

  const submitDocument = async (formData: any) => {
    try {
      setSaveLoading(true);
      const { documents } = formData;

      if (!documents || documents.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No documents to upload",
          confirmButtonText: "Ok",
        });
        setSaveLoading(false);
        return;
      }

      // With valuePropName="fileList", doc.file is now the fileList array directly
      const hasFiles = documents.some((doc: any) => {
        if (Array.isArray(doc.file)) {
          return doc.file.length > 0;
        }
        if (doc.file && doc.file.fileList) {
          return doc.file.fileList.length > 0;
        }
        return false;
      });

      if (!hasFiles) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please select at least one file to upload",
          confirmButtonText: "Ok",
        });
        setSaveLoading(false);
        return;
      }

      // If opened from TaskCreation/IssuesCreation, format as docKeys
      if (openDrawer && onDocumentsSubmit) {
        const docKeys: any[] = [];
        const files: File[] = [];
        let order = 0;

        for (const doc of documents) {
          // With valuePropName="fileList", doc.file is now the fileList array directly
          // Handle both cases: array (new) or object with fileList (old)
          let fileList: any[] = [];
          if (Array.isArray(doc.file)) {
            fileList = doc.file;
          } else if (doc.file && doc.file.fileList) {
            fileList = doc.file.fileList;
          }

          if (fileList && fileList.length > 0) {
            // Process all files in the fileList (multiple files per document)
            for (const fileItem of fileList) {
              if (fileItem.originFileObj) {
                // Check file size (10MB limit)
                if (fileItem.size > 10 * 1024 * 1024) {
                  Swal.fire({
                    icon: "error",
                    title: "File Size Error",
                    text: `File ${fileItem.name} is too large. Maximum size is 10MB.`,
                    confirmButtonText: "Ok",
                  });
                  setSaveLoading(false);
                  return;
                }

                const moduleName = linkType === "task" ? "task" : "issue";

                // Determine linkTo based on linkType
                let linkTo = "";
                if (linkType === "task") {
                  linkTo = taskName || linkToName || "";
                } else if (linkType === "issue") {
                  linkTo = issueName || linkToName || "";
                }

                docKeys.push({
                  module: moduleName,
                  moduleFileType: "attachment",
                  order: order,
                  remarks: doc.remarks || "",
                  customFileName: doc.docName || fileItem.name,
                  linkType: linkType,
                  linkTo: linkTo,
                });

                files.push(fileItem.originFileObj);
                order++;
              }
            }
          }
        }

        if (docKeys.length === 0) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Please upload at least one file",
            confirmButtonText: "Ok",
          });
          setSaveLoading(false);
          return;
        }

        // Call the callback with docKeys and files
        onDocumentsSubmit({ docKeys, files });
        setIsModalVisible(false);
        setSaveLoading(false);
        if (onDrawerClose) {
          onDrawerClose();
        }
        return;
      }

      // Regular document upload flow (existing logic)
      const formDataToSend = new FormData();

      // Prepare the payload according to the API structure
      const newFileData: any[] = [];
      let fileIndex = 0; // Global file index for all files across all documents

      for (const doc of documents) {
        let linkTo = doc.linkTo;
        let phaseId: number | null = null;

        // Convert IDs to names for API payload
        if (doc.linkType === "project") {
          // Get project name from mapping
          linkTo = projectNameMapping[doc.linkTo] || doc.linkTo;
        } else if (doc.linkType === "phase") {
          // For phase documents, use the phase name from context
          linkTo = phaseName || linkToName;
          phaseId = parseInt(linkToId.toString()); // Keep phaseId for backend reference
        } else if (doc.linkType === "activity") {
          // For activity documents, use the activity name from context
          linkTo = activityName || linkToName;
        } else if (doc.linkType === "task") {
          // For task documents, use the task name from context
          linkTo = taskName || linkToName;
        } else if (doc.linkType === "issue") {
          // For issue documents, use the issue name from context
          linkTo = issueName || linkToName;
        }
        // For other link types, use linkTo directly

        // Add files to FormData and create payload items for each file
        // Handle both formats: array (new with valuePropName) or object with fileList (old)
        let fileList: any[] = [];
        if (Array.isArray(doc.file)) {
          fileList = doc.file;
        } else if (doc.file && doc.file.fileList) {
          fileList = doc.file.fileList;
        }

        if (fileList && fileList.length > 0) {
          fileList.forEach((fileItem: any) => {
            try {
              if (fileItem.originFileObj) {
                // Check file size (10MB limit)
                if (fileItem.size > 10 * 1024 * 1024) {
                  Swal.fire({
                    icon: "error",
                    title: "File Size Error",
                    text: `File ${fileItem.name} is too large. Maximum size is 10MB.`,
                    confirmButtonText: "Ok",
                  });
                  return;
                }

                formDataToSend.append(
                  `files`,
                  fileItem.originFileObj,
                  fileItem.name
                );

                // Create a payload item for each file
                const payloadItem: any = {
                  indexOfFileLocated: fileIndex,
                  linkType: linkType,
                  linkTo: linkTo,
                  docName: doc.docName || fileItem.name, // Use file name if doc name is empty
                  remarks: doc.remarks || "",
                  action: "Create",
                };

                // Add appropriate ID based on link type
                if (linkType === "project") {
                  payloadItem.projectId = parseInt(linkToId.toString());
                } else if (linkType === "phase") {
                  payloadItem.phaseId = parseInt(linkToId.toString());
                } else if (linkType === "activity") {
                  payloadItem.activityId = parseInt(linkToId.toString());
                } else if (linkType === "task") {
                  payloadItem.taskId = parseInt(linkToId.toString());
                } else if (linkType === "issue") {
                  payloadItem.issueId = parseInt(linkToId.toString());
                }

                newFileData.push(payloadItem);
                fileIndex++; // Increment global file index
              }
            } catch (fileError) {
              console.error(
                `Error processing file ${fileItem?.name}:`,
                fileError
              );
              Swal.fire({
                icon: "error",
                title: "File Processing Error",
                text: `Error processing file ${
                  fileItem?.name || "unknown"
                }. Please try again.`,
                confirmButtonText: "Ok",
              });
              return;
            }
          });
        }
      }

      const filesData = {
        newfiledata: newFileData,
        deletedFileData: [],
      };

      formDataToSend.append("filesData", JSON.stringify(filesData));

      let response;
      try {
        response = await documentApis.uploadDocument(formDataToSend);
      } catch (apiError: any) {
        console.error("API call failed:", apiError);

        // Extract the actual error message from the API response
        let errorMessage = "API call failed. Please try again.";

        if (apiError?.response?.data) {
          // Try to get the message from response.data
          errorMessage =
            apiError.response.data.message ||
            apiError.response.data.error ||
            errorMessage;
        } else if (apiError?.message) {
          // Fallback to apiError.message
          errorMessage = apiError.message;
        }

        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMessage,
          confirmButtonText: "Ok",
        });
        return;
      }

      if (response && response.status) {
        setIsModalVisible(false);
        setIsAddingToGroup(false);
        form.resetFields();
        setDocumentCount(1);

        if (shouldLoadData) {
          fetchData({
            current: 1,
            pageSize: pagination.pageSize,
            sortOrder: "DESC",
            direction: "P",
            pageDiff: 0,
            paginationId: 10,
          });
        }

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Documents uploaded successfully!",
          confirmButtonText: "Ok",
        }).then(() => {
          if (onDrawerClose) {
            onDrawerClose();
          }
        });
      } else {
        console.error("Document upload failed:", response);

        // Extract the actual error message from the response
        let errorMessage = "Document upload failed. Please try again.";

        if (response?.message) {
          errorMessage = response.message;
        } else if (response?.error) {
          errorMessage = response.error;
        }

        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMessage,
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Document upload error:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: "Document upload error. Please try again.",
        confirmButtonText: "Ok",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // Replace document function
  // Delete document function
  const handleDeleteDocument = (record: any) => {
    // Check if this is a grouped record with multiple documents
    const isGrouped = record.documents && record.documents.length > 1;
    const fileCount = isGrouped ? record.documents.length : 1;

    Swal.fire({
      title: "Delete Entire Document Record?",
      text: isGrouped
        ? `This will delete the entire document record with ${fileCount} files. This action cannot be undone.`
        : "This will delete the entire document record. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete entire record!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for deletion
          const formDataToSend = new FormData();

          // Create payload for deletion
          const filesData = {
            newfiledata: [],
            deletedFileData: [
              {
                docId: record.docId,
              },
            ],
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("Delete API call failed:", apiError);

            // Extract the actual error message from the API response
            let errorMessage = "Delete API call failed. Please try again.";

            if (apiError?.response?.data) {
              // Try to get the message from response.data
              errorMessage =
                apiError.response.data.message ||
                apiError.response.data.error ||
                errorMessage;
            } else if (apiError?.message) {
              // Fallback to apiError.message
              errorMessage = apiError.message;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `Entire document record with ${fileCount} file(s) has been deleted.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("Document delete failed:", response);

            // Extract the actual error message from the response
            let errorMessage = "Document delete failed. Please try again.";

            if (response?.message) {
              errorMessage = response.message;
            } else if (response?.error) {
              errorMessage = response.error;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("Document delete error:", error);
          Swal.fire("Error", "Failed to delete document.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  const showModal = () => {
    setIsModalVisible(true);

    // Pre-select values based on context
    let initialLinkType = linkType;
    let initialLinkTo = undefined;

    if (linkType === "project" && projectName) {
      initialLinkTo = projectName;
    } else if (linkType === "phase" && phaseName) {
      initialLinkTo = phaseName;
    } else if (linkType === "activity" && activityName) {
      initialLinkTo = activityName;
    } else if (linkType === "task" && taskName) {
      initialLinkTo = taskName;
    } else if (linkType === "issue" && issueName) {
      initialLinkTo = issueName;
    }

    // Ensure form is properly initialized before setting values
    try {
      form.resetFields();
      form.setFieldsValue({
        documents: [
          {
            linkType: initialLinkType,
            linkTo: initialLinkTo,
            docName: "",
            remarks: "",
            file: undefined,
          },
        ],
      });
    } catch (error) {
      console.error("Error initializing form:", error);
      // Fallback to basic initialization
      form.resetFields();
    }

    setDocumentCount(1);
    setSelectedLinkTypes([initialLinkType]);
    setSelectedProjectForPhase(projectId || null);

    // No need to fetch phases since we already have phaseName from props
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsAddingToGroup(false);
    form.resetFields();
    setDocumentCount(1);
    if (onDrawerClose) {
      onDrawerClose();
    }
  };

  const handleOk = () => {
    // First check if form is properly initialized
    if (!form) {
      console.error("Form is not initialized");
      return;
    }

    try {
      form
        .validateFields()
        .then((values) => {
          submitDocument(values);
        })
        .catch((errorInfo) => {
          console.error("Form validation failed:", errorInfo);
          // The form will automatically show validation errors
          // No need to show additional error message as Ant Design handles this
        });
    } catch (error) {
      console.error("Unexpected error during form validation:", error);
      // Show a user-friendly error message using SweetAlert
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred. Please try again.",
        confirmButtonText: "Ok",
      });
    }
  };

  // Replace modal handlers

  const removeDocument = (index: number) => {
    if (documentCount > 1) {
      setDocumentCount((prev) => prev - 1);
      const currentValues = form.getFieldsValue();
      const documents = currentValues.documents || [];
      documents.splice(index, 1);
      form.setFieldsValue({ documents });
    }
  };

  const addDocument = () => {
    setDocumentCount((prev) => prev + 1);
    const currentValues = form.getFieldsValue();
    const documents = currentValues.documents || [];
    documents.push({
      linkType: linkType,
      linkTo: undefined,
      docName: "",
      remarks: "",
      file: undefined,
    });
    form.setFieldsValue({ documents });
  };

  // Delete individual file function
  const handleDeleteFile = (document: any, fileIndex: number) => {
    const fileName = document.docName || document.customDocName || "this file";

    Swal.fire({
      title: "Delete File?",
      text: `Do you want to delete "${fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete this file!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for file deletion
          const formDataToSend = new FormData();

          // Create payload for file deletion
          const filesData = {
            newfiledata: [],
            deletedFileData: [
              {
                docId: document.docId,
                fileIndex: fileIndex,
              },
            ],
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("File delete API call failed:", apiError);

            // Extract the actual error message from the API response
            let errorMessage = "File delete API call failed. Please try again.";

            if (apiError?.response?.data) {
              // Try to get the message from response.data
              errorMessage =
                apiError.response.data.message ||
                apiError.response.data.error ||
                errorMessage;
            } else if (apiError?.message) {
              // Fallback to apiError.message
              errorMessage = apiError.message;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `File "${fileName}" has been deleted.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("File delete failed:", response);

            // Extract the actual error message from the response
            let errorMessage = "File delete failed. Please try again.";

            if (response?.message) {
              errorMessage = response.message;
            } else if (response?.error) {
              errorMessage = response.error;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("File delete error:", error);
          Swal.fire("Error", "Failed to delete file.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Open drawer when openDrawer prop changes to true
  useEffect(() => {
    if (openDrawer) {
      setIsModalVisible(true);
      // Initialize form values for the drawer
      let initialLinkType = linkType;
      let initialLinkTo: string | undefined = undefined;

      if (linkType === "project" && projectName) {
        initialLinkTo = projectName;
      } else if (linkType === "phase" && phaseName) {
        initialLinkTo = phaseName;
      } else if (linkType === "activity" && activityName) {
        initialLinkTo = activityName;
      } else if (linkType === "task" && taskName) {
        initialLinkTo = taskName;
      } else if (linkType === "issue" && issueName) {
        initialLinkTo = issueName;
      }

      try {
        form.resetFields();

        // If initialDocData exists, restore previous document entries
        if (
          initialDocData &&
          initialDocData.docKeys &&
          initialDocData.docKeys.length > 0
        ) {
          const restoredDocuments = initialDocData.docKeys.map(
            (docKey, index) => {
              // Create file list from initial files if available
              let fileList: any[] = [];
              if (initialDocData.files && initialDocData.files[index]) {
                const file = initialDocData.files[index];
                // Verify file is a valid File object
                if (file instanceof File) {
                  // Ensure all required properties are present for Ant Design Upload
                  fileList = [
                    {
                      uid: `restored-${index}-${Date.now()}-${file.name}`,
                      name: docKey.customFileName || file.name,
                      status: "done" as const,
                      originFileObj: file,
                      size: file.size || 0,
                      type: file.type || "",
                      url: undefined, // No URL needed for local files
                    },
                  ];
                }
              }

              return {
                linkType: initialLinkType,
                linkTo: initialLinkTo,
                docName: docKey.customFileName || "",
                remarks: docKey.remarks || "",
                // With valuePropName="fileList", the form field value should be the fileList array directly
                file: fileList.length > 0 ? fileList : undefined,
              };
            }
          );

          // Set document count first
          setDocumentCount(restoredDocuments.length);

          // Use setTimeout to ensure form is ready before setting values
          setTimeout(() => {
            try {
              // First, set all form values at once
              const fieldsValue: any = {
                documents: restoredDocuments,
              };
              form.setFieldsValue(fieldsValue);

              // Then, explicitly set each field to ensure form re-renders correctly
              restoredDocuments.forEach((doc, idx) => {
                form.setFieldsValue({
                  [`documents[${idx}].docName`]: doc.docName,
                  [`documents[${idx}].remarks`]: doc.remarks,
                  [`documents[${idx}].linkType`]: doc.linkType,
                  [`documents[${idx}].linkTo`]: doc.linkTo,
                });

                // Explicitly set file field with proper structure
                // With valuePropName="fileList", we set the fileList array directly
                if (
                  doc.file &&
                  Array.isArray(doc.file) &&
                  doc.file.length > 0
                ) {
                  // Set the file field value directly as the fileList array
                  form.setFieldValue(["documents", idx, "file"], doc.file);
                  // Try setting it again after a small delay to ensure it sticks
                  setTimeout(() => {
                    if (doc.file && Array.isArray(doc.file)) {
                      form.setFieldValue(["documents", idx, "file"], doc.file);
                    }
                  }, 50);
                }
              });

              // Force form to re-validate and update
              form.validateFields().catch(() => {
                // Ignore validation errors
              });
            } catch (error) {
              console.error("Error setting form values:", error);
            }
          }, 200);
        } else {
          // Default: start with empty form
          form.setFieldsValue({
            documents: [
              {
                linkType: initialLinkType,
                linkTo: initialLinkTo,
                docName: "",
                remarks: "",
                file: undefined,
              },
            ],
          });
          setDocumentCount(1);
        }
      } catch (error) {
        console.error("Error initializing form:", error);
      }

      setSelectedLinkTypes([initialLinkType]);
      setSelectedProjectForPhase(projectId || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openDrawer,
    linkType,
    projectName,
    phaseName,
    activityName,
    taskName,
    issueName,
    projectId,
    initialDocData,
  ]);

  // Load data on component mount only when shouldLoadData is true
  useEffect(() => {
    if (!shouldLoadData) return;

    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: 10,
      searchQuery: searchQuery,
    });

    // Fetch project list only for project documents or when needed for dropdowns
    if (linkType === "project") {
      fetchProjectList();
    }

    // No need to fetch phase list for phase documents since we already have phaseName from props

    // No need to fetch task list for task documents since we already have taskName from props
  }, [linkType, linkToId, projectId, shouldLoadData]); // Removed searchQuery from dependency array

  // Handle screen size changes
  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  // Table change handler
  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize !== pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current !== pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        const lastItemId = dataSource[dataSource.length - 1]?.paginationId;
        const firstItemId = dataSource[0]?.paginationId;
        apiParams.paginationId =
          apiParams.direction === "F"
            ? lastItemId || null // Last item ID for backward direction
            : firstItemId || null; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  // Table columns configuration
  const columns = [
    // {
    //   title: "Document Id",
    //   dataIndex: "docId",
    //   key: "docId",
    //   width: 80,
    //   fixed: "left" as const,
    // },
    {
      title: "Document Name",
      dataIndex: "customDocName",
      key: "customDocName",
      width: 150,
      ellipsis: true,
      render: (text: string, record: any) => {
        // If this is a grouped record, show the grouped information
        if (record.docCount && record.docCount > 1) {
          const tooltipText = record.documents
            ?.map((doc: any) => doc.docName)
            .join(", ");
          return (
            <div title={tooltipText}>
              {record.customDocName || record.docNames}
            </div>
          );
        }
        // Otherwise show the single document name with customDocName, hover shows docName
        const displayName = record.customDocName || record.docName || text;
        return <span title={record.docName}>{displayName}</span>;
      },
    },
    {
      title: "Link Type",
      dataIndex: "linkType",
      key: "linkType",
      width: 100,
      render: (text: string) => (
        <span style={{ textTransform: "capitalize" }}>{text}</span>
      ),
    },
    {
      title: "Link To",
      dataIndex: "linkTo",
      key: "linkTo",
      width: 120,
      ellipsis: true,
      render: (text: string) => (
        <span style={{ textTransform: "capitalize" }}>
          {text && text.trim() ? text : "N/A"}
        </span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      ellipsis: true,
      render: (text: string) => text || "N/A",
    },
    ...(!isFromWorkUI
      ? [
          {
            title: "Actions",
            key: "filePath",
            width: 100,
            fixed: "right" as const,
            render: (_: any, record: any) => {
              // If this is a grouped record, show actions for all documents
              if (record.documents && record.documents.length > 1) {
                return (
                  <Space size="small">
                    <Dropdown
                      menu={{
                        items: record.documents.map(
                          (doc: any, index: number) => ({
                            key: index,
                            label: (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Icon name="eye" style={{ color: "#4169E1" }} />
                                <span>{doc.docName}</span>
                              </div>
                            ),
                            onClick: () => {
                              // For view, open in new tab
                              window.open(doc.filePath, "_blank");
                            },
                          })
                        ),
                      }}
                      placement="bottomRight"
                      trigger={["hover"]}
                    >
                      <span
                        style={{
                          cursor: "pointer",
                          color: "#4169E1",
                          fontSize: "18px",
                          display: "flex",
                          alignItems: "center",
                          marginRight: "8px",
                        }}
                        title={`View ${record.documents.length} Documents`}
                      >
                        <Icon name="eye" />
                      </span>
                    </Dropdown>

                    <Dropdown
                      menu={{
                        items: [
                          // Option to download all files at once
                          {
                            key: "download-all",
                            label: (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#52c41a",
                                  fontWeight: "500",
                                }}
                              >
                                <Icon
                                  name="download"
                                  style={{ color: "#52c41a" }}
                                />
                                <span>
                                  Download All Files ({record.documents.length}{" "}
                                  files)
                                </span>
                              </div>
                            ),
                            onClick: () => {
                              // Download all files
                              record.documents.forEach((doc: any) => {
                                downloadDocument(doc.filePath, doc.docName);
                              });
                            },
                          },
                          // Divider
                          {
                            type: "divider",
                          },
                          // Individual file download options
                          ...record.documents.map(
                            (doc: any, index: number) => ({
                              key: index,
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                    position: "relative",
                                    minHeight: "24px",
                                  }}
                                  className="file-row-hover"
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#666",
                                      flex: 1,
                                    }}
                                  >
                                    {doc.docName}
                                  </span>
                                  <Icon
                                    name="download"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "14px",
                                      marginLeft: "8px",
                                    }}
                                    className="download-icon"
                                    onClick={(
                                      e: React.MouseEvent<HTMLElement>
                                    ) => {
                                      e.stopPropagation();
                                      downloadDocument(
                                        doc.filePath,
                                        doc.docName
                                      );
                                    }}
                                  />
                                </div>
                              ),
                              onClick: () => {
                                // For download, trigger file download
                                downloadDocument(doc.filePath, doc.docName);
                              },
                            })
                          ),
                        ],
                      }}
                      placement="bottomRight"
                      trigger={["hover"]}
                    >
                      <span
                        style={{
                          cursor: "pointer",
                          color: "#52c41a",
                          fontSize: "18px",
                          display: "flex",
                          alignItems: "center",
                          marginRight: "8px",
                        }}
                        title={`Download ${record.documents.length} Documents`}
                      >
                        <Icon name="download" />
                      </span>
                    </Dropdown>

                    {/* Add More Documents Button for Grouped Documents */}
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#4169E1",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "8px",
                      }}
                      title="Add More Documents to This Group"
                      onClick={() => {
                        // Open the upload modal with pre-filled document name
                        setIsAddingToGroup(true);
                        form.setFieldsValue({
                          documents: [
                            {
                              linkType: record.linkType,
                              linkTo: record.linkTo,
                              project:
                                record.linkType === "project"
                                  ? record.linkTo
                                  : undefined,
                              phase:
                                record.linkType === "phase"
                                  ? record.linkTo
                                  : undefined,
                              activity:
                                record.linkType === "activity"
                                  ? record.linkTo
                                  : undefined,
                              task:
                                record.linkType === "task"
                                  ? record.linkTo
                                  : undefined,
                              issue:
                                record.linkType === "issue"
                                  ? record.linkTo
                                  : undefined,
                              docName: record.customDocName || record.docNames,
                              remarks: record.remarks || "",
                              file: undefined,
                            },
                          ],
                        });
                        setIsModalVisible(true);
                      }}
                    >
                      <Icon name="plus-circle" />
                    </span>

                    {linkType === "project" && (
                      <CancelAccess moduleName="Projects">
                        <Dropdown
                          menu={{
                            items: [
                              // Option to delete entire document record (only show if multiple files)
                              ...(record.documents.length > 1
                                ? [
                                    {
                                      key: "delete-all",
                                      label: (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            color: "#ff4d4f",
                                            fontWeight: "500",
                                          }}
                                        >
                                          <Icon
                                            name="trash"
                                            style={{ color: "#ff4d4f" }}
                                          />
                                          <span>
                                            Delete All Files (
                                            {record.documents.length} files)
                                          </span>
                                        </div>
                                      ),
                                      onClick: () =>
                                        handleDeleteAllFiles(record),
                                    },
                                    // Divider only if multiple files
                                    {
                                      type: "divider",
                                    },
                                  ]
                                : []),
                              // Individual file delete options
                              ...record.documents.map(
                                (doc: any, index: number) => ({
                                  key: `delete-file-${index}`,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        minHeight: "24px",
                                      }}
                                      className="file-row-hover"
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          flex: 1,
                                        }}
                                      >
                                        {doc.docName}
                                      </span>
                                      <Icon
                                        name="trash"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          marginLeft: "8px",
                                        }}
                                        className="delete-icon"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>
                                        ) => {
                                          e.stopPropagation();
                                          handleDeleteFile(doc, index);
                                        }}
                                      />
                                    </div>
                                  ),
                                  onClick: () => {
                                    // Delete the individual file
                                    handleDeleteFile(doc, index);
                                  },
                                })
                              ),
                            ],
                          }}
                          placement="bottomRight"
                          trigger={["hover"]}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#ff4d4f",
                              fontSize: "18px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Delete Options"
                          >
                            <Icon name="trash" />
                          </span>
                        </Dropdown>
                      </CancelAccess>
                    )}
                    {linkType === "phase" && (
                      <CancelAccess moduleName="Phases">
                        <Dropdown
                          menu={{
                            items: [
                              // Option to delete entire document record (only show if multiple files)
                              ...(record.documents.length > 1
                                ? [
                                    {
                                      key: "delete-all",
                                      label: (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            color: "#ff4d4f",
                                            fontWeight: "500",
                                          }}
                                        >
                                          <Icon
                                            name="trash"
                                            style={{ color: "#ff4d4f" }}
                                          />
                                          <span>
                                            Delete All Files (
                                            {record.documents.length} files)
                                          </span>
                                        </div>
                                      ),
                                      onClick: () =>
                                        handleDeleteAllFiles(record),
                                    },
                                    // Divider only if multiple files
                                    {
                                      type: "divider",
                                    },
                                  ]
                                : []),
                              // Individual file delete options
                              ...record.documents.map(
                                (doc: any, index: number) => ({
                                  key: `delete-file-${index}`,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        minHeight: "24px",
                                      }}
                                      className="file-row-hover"
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          flex: 1,
                                        }}
                                      >
                                        {doc.docName}
                                      </span>
                                      <Icon
                                        name="trash"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          marginLeft: "8px",
                                        }}
                                        className="delete-icon"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>
                                        ) => {
                                          e.stopPropagation();
                                          handleDeleteFile(doc, index);
                                        }}
                                      />
                                    </div>
                                  ),
                                  onClick: () => {
                                    // Delete the individual file
                                    handleDeleteFile(doc, index);
                                  },
                                })
                              ),
                            ],
                          }}
                          placement="bottomRight"
                          trigger={["hover"]}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#ff4d4f",
                              fontSize: "18px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Delete Options"
                          >
                            <Icon name="trash" />
                          </span>
                        </Dropdown>
                      </CancelAccess>
                    )}
                    {linkType === "activity" && (
                      <CancelAccess moduleName="Activities">
                        <Dropdown
                          menu={{
                            items: [
                              // Option to delete entire document record (only show if multiple files)
                              ...(record.documents.length > 1
                                ? [
                                    {
                                      key: "delete-all",
                                      label: (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            color: "#ff4d4f",
                                            fontWeight: "500",
                                          }}
                                        >
                                          <Icon
                                            name="trash"
                                            style={{ color: "#ff4d4f" }}
                                          />
                                          <span>
                                            Delete All Files (
                                            {record.documents.length} files)
                                          </span>
                                        </div>
                                      ),
                                      onClick: () =>
                                        handleDeleteAllFiles(record),
                                    },
                                    // Divider only if multiple files
                                    {
                                      type: "divider",
                                    },
                                  ]
                                : []),
                              // Individual file delete options
                              ...record.documents.map(
                                (doc: any, index: number) => ({
                                  key: `delete-file-${index}`,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        minHeight: "24px",
                                      }}
                                      className="file-row-hover"
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          flex: 1,
                                        }}
                                      >
                                        {doc.docName}
                                      </span>
                                      <Icon
                                        name="trash"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          marginLeft: "8px",
                                        }}
                                        className="delete-icon"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>
                                        ) => {
                                          e.stopPropagation();
                                          handleDeleteFile(doc, index);
                                        }}
                                      />
                                    </div>
                                  ),
                                  onClick: () => {
                                    // Delete the individual file
                                    handleDeleteFile(doc, index);
                                  },
                                })
                              ),
                            ],
                          }}
                          placement="bottomRight"
                          trigger={["hover"]}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#ff4d4f",
                              fontSize: "18px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Delete Options"
                          >
                            <Icon name="trash" />
                          </span>
                        </Dropdown>
                      </CancelAccess>
                    )}
                    {linkType === "task" && (
                      <CancelAccess moduleName="Tasks">
                        <Dropdown
                          menu={{
                            items: [
                              // Option to delete entire document record (only show if multiple files)
                              ...(record.documents.length > 1
                                ? [
                                    {
                                      key: "delete-all",
                                      label: (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            color: "#ff4d4f",
                                            fontWeight: "500",
                                          }}
                                        >
                                          <Icon
                                            name="trash"
                                            style={{ color: "#ff4d4f" }}
                                          />
                                          <span>
                                            Delete All Files (
                                            {record.documents.length} files)
                                          </span>
                                        </div>
                                      ),
                                      onClick: () =>
                                        handleDeleteAllFiles(record),
                                    },
                                    // Divider only if multiple files
                                    {
                                      type: "divider",
                                    },
                                  ]
                                : []),
                              // Individual file delete options
                              ...record.documents.map(
                                (doc: any, index: number) => ({
                                  key: `delete-file-${index}`,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        minHeight: "24px",
                                      }}
                                      className="file-row-hover"
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          flex: 1,
                                        }}
                                      >
                                        {doc.docName}
                                      </span>
                                      <Icon
                                        name="trash"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          marginLeft: "8px",
                                        }}
                                        className="delete-icon"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>
                                        ) => {
                                          e.stopPropagation();
                                          handleDeleteFile(doc, index);
                                        }}
                                      />
                                    </div>
                                  ),
                                  onClick: () => {
                                    // Delete the individual file
                                    handleDeleteFile(doc, index);
                                  },
                                })
                              ),
                            ],
                          }}
                          placement="bottomRight"
                          trigger={["hover"]}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#ff4d4f",
                              fontSize: "18px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Delete Options"
                          >
                            <Icon name="trash" />
                          </span>
                        </Dropdown>
                      </CancelAccess>
                    )}
                    {linkType === "issue" && (
                      <CancelAccess moduleName="Issues">
                        <Dropdown
                          menu={{
                            items: [
                              // Option to delete entire document record (only show if multiple files)
                              ...(record.documents.length > 1
                                ? [
                                    {
                                      key: "delete-all",
                                      label: (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            color: "#ff4d4f",
                                            fontWeight: "500",
                                          }}
                                        >
                                          <Icon
                                            name="trash"
                                            style={{ color: "#ff4d4f" }}
                                          />
                                          <span>
                                            Delete All Files (
                                            {record.documents.length} files)
                                          </span>
                                        </div>
                                      ),
                                      onClick: () =>
                                        handleDeleteAllFiles(record),
                                    },
                                    // Divider only if multiple files
                                    {
                                      type: "divider",
                                    },
                                  ]
                                : []),
                              // Individual file delete options
                              ...record.documents.map(
                                (doc: any, index: number) => ({
                                  key: `delete-file-${index}`,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        position: "relative",
                                        minHeight: "24px",
                                      }}
                                      className="file-row-hover"
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                          flex: 1,
                                        }}
                                      >
                                        {doc.docName}
                                      </span>
                                      <Icon
                                        name="trash"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          marginLeft: "8px",
                                        }}
                                        className="delete-icon"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>
                                        ) => {
                                          e.stopPropagation();
                                          handleDeleteFile(doc, index);
                                        }}
                                      />
                                    </div>
                                  ),
                                  onClick: () => {
                                    // Delete the individual file
                                    handleDeleteFile(doc, index);
                                  },
                                })
                              ),
                            ],
                          }}
                          placement="bottomRight"
                          trigger={["hover"]}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#ff4d4f",
                              fontSize: "18px",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Delete Options"
                          >
                            <Icon name="trash" />
                          </span>
                        </Dropdown>
                      </CancelAccess>
                    )}
                  </Space>
                );
              }

              // For single documents, show dropdowns with file name
              return (
                <Space size="small">
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "view",
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Icon name="eye" style={{ color: "#4169E1" }} />
                              <span>
                                {record.docName || record.customDocName}
                              </span>
                            </div>
                          ),
                          onClick: () => window.open(record.filePath, "_blank"),
                        },
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#4169E1",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "8px",
                      }}
                      title="View Document"
                    >
                      <Icon name="eye" />
                    </span>
                  </Dropdown>

                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "download",
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Icon
                                name="download"
                                style={{ color: "#52c41a" }}
                              />
                              <span>
                                {record.docName || record.customDocName}
                              </span>
                            </div>
                          ),
                          onClick: () =>
                            downloadDocument(
                              record.filePath,
                              record.docName || record.customDocName
                            ),
                        },
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#52c41a",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "8px",
                      }}
                      title="Download Document"
                    >
                      <Icon name="download" />
                    </span>
                  </Dropdown>

                  {/* Add More Documents Button for Single Documents */}
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#4169E1",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                      marginRight: "8px",
                    }}
                    title="Add More Documents to This Group"
                    onClick={() => {
                      // Open the upload modal with pre-filled document name
                      setIsAddingToGroup(true);
                      form.setFieldsValue({
                        documents: [
                          {
                            linkType: record.linkType,
                            linkTo: record.linkTo,
                            project:
                              record.linkType === "project"
                                ? record.linkTo
                                : undefined,
                            phase:
                              record.linkType === "phase"
                                ? record.linkTo
                                : undefined,
                            activity:
                              record.linkType === "activity"
                                ? record.linkTo
                                : undefined,
                            task:
                              record.linkType === "task"
                                ? record.linkTo
                                : undefined,
                            issue:
                              record.linkType === "issue"
                                ? record.linkTo
                                : undefined,
                            docName: record.customDocName || record.docName,
                            remarks: record.remarks || "",
                            file: undefined,
                          },
                        ],
                      });
                      setIsModalVisible(true);
                    }}
                  >
                    <Icon name="plus-circle" />
                  </span>
                  {linkType === "project" && (
                    <CancelAccess moduleName="Projects">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "delete",
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <Icon
                                    name="trash"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {record.docName || record.customDocName}
                                  </span>
                                </div>
                              ),
                              onClick: () => handleDeleteDocument(record),
                            },
                          ],
                        }}
                        placement="bottomRight"
                        trigger={["hover"]}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete Document"
                        >
                          <Icon name="trash" />
                        </span>
                      </Dropdown>
                    </CancelAccess>
                  )}
                  {linkType === "phase" && (
                    <CancelAccess moduleName="Phases">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "delete",
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <Icon
                                    name="trash"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {record.docName || record.customDocName}
                                  </span>
                                </div>
                              ),
                              onClick: () => handleDeleteDocument(record),
                            },
                          ],
                        }}
                        placement="bottomRight"
                        trigger={["hover"]}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete Document"
                        >
                          <Icon name="trash" />
                        </span>
                      </Dropdown>
                    </CancelAccess>
                  )}
                  {linkType === "activity" && (
                    <CancelAccess moduleName="Activities">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "delete",
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <Icon
                                    name="trash"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {record.docName || record.customDocName}
                                  </span>
                                </div>
                              ),
                              onClick: () => handleDeleteDocument(record),
                            },
                          ],
                        }}
                        placement="bottomRight"
                        trigger={["hover"]}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete Document"
                        >
                          <Icon name="trash" />
                        </span>
                      </Dropdown>
                    </CancelAccess>
                  )}
                  {linkType === "task" && (
                    <CancelAccess moduleName="Tasks">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "delete",
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <Icon
                                    name="trash"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {record.docName || record.customDocName}
                                  </span>
                                </div>
                              ),
                              onClick: () => handleDeleteDocument(record),
                            },
                          ],
                        }}
                        placement="bottomRight"
                        trigger={["hover"]}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete Document"
                        >
                          <Icon name="trash" />
                        </span>
                      </Dropdown>
                    </CancelAccess>
                  )}
                  {linkType === "issue" && (
                    <CancelAccess moduleName="Issues">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "delete",
                              label: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <Icon
                                    name="trash"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {record.docName || record.customDocName}
                                  </span>
                                </div>
                              ),
                              onClick: () => handleDeleteDocument(record),
                            },
                          ],
                        }}
                        placement="bottomRight"
                        trigger={["hover"]}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete Document"
                        >
                          <Icon name="trash" />
                        </span>
                      </Dropdown>
                    </CancelAccess>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <FormProvider {...methods}>
      {!openDrawer && (
        <div className="card-inner card-inner-lg p-0">
          <div className="content clearfix">
            <div
              style={{
                width:
                  linkType === "task"
                    ? "100%"
                    : screenSize > 0
                    ? screenSize - 120 + "px"
                    : "100%",
                maxWidth: "100%",
                margin: "10px auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                {linkType === "project" && (
                  <h4 style={{ margin: 0, color: "#262626" }}>
                    {projectName ||
                      linkType.charAt(0).toUpperCase() +
                        linkType.slice(1) +
                        " Documents"}
                  </h4>
                )}
                {(linkType === "phase" ||
                  linkType === "activity" ||
                  linkType === "task" ||
                  linkType === "issue") && (
                  <div className="nk-ibx-head-actions w-45 ms-1">
                    <div className="nk-ibx-head-tools g-1 m-0 p-0 w-100">
                      <div className="w-100 m-0">
                        <SearchInput
                          name="search"
                          placeholder={`Search ${linkType} documents...`}
                          width="100%"
                          prefixIcon={<SearchOutlined />}
                          onDebouncedSearch={handleSearch}
                          debounceDelay={500}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {/* {linkType === "issue" && (
                <h4 style={{ margin: 0, color: "#262626" }}>
                  {issueName ||
                    linkType.charAt(0).toUpperCase() +
                      linkType.slice(1) +
                      " Documents"}
                </h4>
              )} */}
                {linkType === "project" &&
                  canAdd(accessibility, "Projects") &&
                  !isFromWorkUI && (
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={showModal}
                    >
                      <Icon name={"plus"} />
                      <span>Add Document</span>
                    </Button>
                  )}
                {linkType === "phase" &&
                  canAdd(accessibility, "Phases") &&
                  !isFromWorkUI && (
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={showModal}
                    >
                      <Icon name={"plus"} />
                      <span>Add Document</span>
                    </Button>
                  )}
                {linkType === "activity" &&
                  canAdd(accessibility, "Activities") &&
                  !isFromWorkUI && (
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={showModal}
                    >
                      <Icon name={"plus"} />
                      <span>Add Document</span>
                    </Button>
                  )}
                {linkType === "task" &&
                  canAdd(accessibility, "Tasks") &&
                  !isFromWorkUI && (
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={showModal}
                    >
                      <Icon name={"plus"} />
                      <span>Add Document</span>
                    </Button>
                  )}
                {linkType === "issue" &&
                  canAdd(accessibility, "Issues") &&
                  !isFromWorkUI && (
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={showModal}
                    >
                      <Icon name={"plus"} />
                      <span>Add Document</span>
                    </Button>
                  )}
              </div>

              <SkeletonTable
                loading={loading}
                columns={columns as SkeletonTableColumnsType[]}
              >
                <Table
                  rowKey="docId"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    // showQuickJumper: true, // Commented out "Go to page" functionality
                  }}
                  onChange={tableOnChange}
                  bordered
                  size="small"
                  scroll={{
                    x: 700,
                  }}
                />
              </SkeletonTable>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Drawer */}
      {(!isFromWorkUI || openDrawer) && (
        <Drawer
          title={
            <div
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Add New Document
            </div>
          }
          open={openDrawer || isModalVisible}
          onClose={handleCancel}
          width={parentDrawerMode ? 700 : 800}
          placement="right"
          push={parentDrawerMode ? { distance: 180 } : false}
          styles={{
            body: {
              padding: "24px",
              backgroundColor: "#fafafa",
            },
            header: {
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 24px",
            },
          }}
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 0",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Button
                onClick={handleCancel}
                style={{
                  borderRadius: "6px",
                  height: "36px",
                }}
              >
                Cancel
              </Button>
              {saveLoading ? (
                <Button
                  color="primary"
                  type="button"
                  style={{
                    borderRadius: "6px",
                    height: "36px",
                    fontSize: "12px",
                    padding: "4px 12px",
                  }}
                >
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  className="toggle"
                  color="primary"
                  size="sm"
                  onClick={handleOk}
                  disabled={
                    openDrawer &&
                    ((linkType === "task" && !taskName && !linkToName) ||
                      (linkType === "issue" && !issueName && !linkToName))
                  }
                  style={{
                    borderRadius: "6px",
                    height: "36px",
                    fontSize: "12px",
                    padding: "4px 12px",
                  }}
                >
                  <Icon name={"save"} />
                  <span>Save Documents</span>
                </Button>
              )}
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              documents: [
                {
                  linkType: linkType, // Pre-select based on context
                  linkTo: undefined,
                  project: undefined,
                  phase: undefined,
                  docName: "",
                  remarks: "",
                  file: undefined,
                },
              ],
            }}
            onValuesChange={(changedValues) => {
              try {
                // Update selectedLinkTypes when form values change
                if (changedValues.documents) {
                  const newSelectedLinkTypes = changedValues.documents.map(
                    (doc: any) => doc.linkType || ""
                  );
                  setSelectedLinkTypes(newSelectedLinkTypes);
                }

                // Also handle when linkType changes directly
                if (
                  changedValues.documents &&
                  changedValues.documents[0] &&
                  changedValues.documents[0].linkType
                ) {
                  const linkType = changedValues.documents[0].linkType;
                  setSelectedLinkTypes([linkType]);
                }
              } catch (error) {
                console.error("Error in onValuesChange:", error);
              }
            }}
          >
            {/* Show document name info when adding to group */}
            {isAddingToGroup && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px 16px",
                  backgroundColor: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: "6px",
                  fontSize: "14px",
                  color: "#52c41a",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InfoCircleOutlined
                  style={{ marginRight: "8px", fontSize: "16px" }}
                />
                Adding files to existing document:{" "}
                <strong style={{ marginLeft: "4px" }}>
                  {form.getFieldValue(["documents", 0, "docName"])}
                </strong>
              </div>
            )}

            {/* Show warning message if task/issue name is empty when opened from TaskCreation/IssuesCreation */}
            {openDrawer &&
            ((linkType === "task" && !taskName && !linkToName) ||
              (linkType === "issue" && !issueName && !linkToName)) ? (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "16px",
                  backgroundColor: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#d46b08",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <InfoCircleOutlined style={{ fontSize: "18px" }} />
                <span>
                  <strong>
                    Please set {linkType === "task" ? "task" : "issue"} name
                    first
                  </strong>{" "}
                  before uploading documents. Go back and enter the{" "}
                  {linkType === "task" ? "task" : "issue"} name in the form.
                </span>
              </div>
            ) : null}

            {Array.from({ length: documentCount }, (_, index) => {
              // Check if upload should be disabled
              const isUploadDisabled =
                openDrawer &&
                ((linkType === "task" && !taskName && !linkToName) ||
                  (linkType === "issue" && !issueName && !linkToName));

              return (
                <div
                  key={index}
                  style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: "8px",
                    padding: "20px",
                    marginBottom: "16px",
                    backgroundColor: "#fff",
                    position: "relative",
                    ...(isUploadDisabled && {
                      opacity: 0.6,
                      pointerEvents: "none",
                    }),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingBottom: "12px",
                    }}
                  >
                    {documentCount > 1 && (
                      <AntButton
                        type="text"
                        onClick={() => removeDocument(index)}
                        icon={<DeleteOutlined style={{ fontSize: "14px" }} />}
                        style={{
                          color: "#666666",
                          border: "1px solid #e8e8e8",
                          borderRadius: "6px",
                          backgroundColor: "#ffffff",
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#ff4d4f";
                          e.currentTarget.style.borderColor = "#ff4d4f";
                          e.currentTarget.style.backgroundColor = "#fff2f0";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#666666";
                          e.currentTarget.style.borderColor = "#e8e8e8";
                          e.currentTarget.style.backgroundColor = "#ffffff";
                        }}
                      />
                    )}
                  </div>

                  {!openDrawer && (
                    <Row gutter={[24, 16]}>
                      <Col span={12}>
                        <Form.Item
                          name={["documents", index, "linkType"]}
                          label={
                            <span style={{ fontWeight: 500, color: "#262626" }}>
                              Link Type
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please select link type",
                              validator: (_, value) => {
                                if (
                                  !value ||
                                  value === undefined ||
                                  value === ""
                                ) {
                                  return Promise.reject(
                                    new Error("Please select link type")
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            value={
                              linkType.charAt(0).toUpperCase() +
                              linkType.slice(1)
                            }
                            disabled
                            style={{ borderRadius: "6px" }}
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        {/* Show Link To field for all contexts */}
                        <Form.Item
                          name={["documents", index, "linkTo"]}
                          label={
                            <span style={{ fontWeight: 500, color: "#262626" }}>
                              Link To
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please select link to",
                              validator: (_, value) => {
                                if (
                                  !value ||
                                  value === undefined ||
                                  value === ""
                                ) {
                                  return Promise.reject(
                                    new Error("Please select link to")
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            value={
                              linkType === "project"
                                ? projectName || linkToName
                                : linkType === "phase"
                                ? phaseName || linkToName
                                : linkType === "activity"
                                ? activityName || linkToName
                                : linkType === "task"
                                ? taskName || linkToName
                                : linkType === "issue"
                                ? issueName || linkToName
                                : linkToName
                            }
                            disabled
                            style={{ borderRadius: "6px" }}
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Row gutter={[24, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name={["documents", index, "docName"]}
                        label={
                          <span style={{ fontWeight: 500, color: "#262626" }}>
                            Document Name
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please enter document name",
                            validator: (_, value) => {
                              if (!value || value.trim() === "") {
                                return Promise.reject(
                                  new Error("Please enter document name")
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder={
                            openDrawer &&
                            ((linkType === "task" &&
                              !taskName &&
                              !linkToName) ||
                              (linkType === "issue" &&
                                !issueName &&
                                !linkToName))
                              ? `Please set ${linkType} name first before uploading documents`
                              : "Enter document name"
                          }
                          style={{
                            borderRadius: "6px",
                          }}
                          size="large"
                          disabled={
                            isAddingToGroup ||
                            (openDrawer &&
                              ((linkType === "task" &&
                                !taskName &&
                                !linkToName) ||
                                (linkType === "issue" &&
                                  !issueName &&
                                  !linkToName)))
                          }
                          className={isAddingToGroup ? "disabled-doc-name" : ""}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={["documents", index, "file"]}
                        label={
                          <span style={{ fontWeight: 500, color: "#262626" }}>
                            File Upload (Multiple)
                          </span>
                        }
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                          // If e is an array, return it directly (when setting programmatically)
                          if (Array.isArray(e)) {
                            return e;
                          }
                          // If e is an Upload event, extract fileList
                          if (e && e.fileList) {
                            return e.fileList;
                          }
                          // Otherwise, return empty array
                          return [];
                        }}
                        rules={[
                          {
                            required: true,
                            message: "Please upload at least one file",
                            validator: (_, value) => {
                              // With valuePropName="fileList", value is now the fileList array directly
                              if (
                                !value ||
                                !Array.isArray(value) ||
                                value.length === 0
                              ) {
                                return Promise.reject(
                                  new Error("Please upload at least one file")
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Upload
                          beforeUpload={() => false}
                          multiple={true}
                          maxCount={5}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                          listType="text"
                          disabled={
                            openDrawer &&
                            ((linkType === "task" &&
                              !taskName &&
                              !linkToName) ||
                              (linkType === "issue" &&
                                !issueName &&
                                !linkToName))
                          }
                          showUploadList={{
                            showPreviewIcon: true,
                            showRemoveIcon: true,
                            showDownloadIcon: true,
                          }}
                        >
                          <AntButton
                            icon={<UploadOutlined />}
                            size="large"
                            disabled={
                              openDrawer &&
                              ((linkType === "task" &&
                                !taskName &&
                                !linkToName) ||
                                (linkType === "issue" &&
                                  !issueName &&
                                  !linkToName))
                            }
                            style={{
                              borderRadius: "4px",
                              height: "36px",
                              backgroundColor:
                                openDrawer &&
                                ((linkType === "task" &&
                                  !taskName &&
                                  !linkToName) ||
                                  (linkType === "issue" &&
                                    !issueName &&
                                    !linkToName))
                                  ? "#f5f5f5"
                                  : "#ffffff",
                              borderColor: "#d9d9d9",
                              color:
                                openDrawer &&
                                ((linkType === "task" &&
                                  !taskName &&
                                  !linkToName) ||
                                  (linkType === "issue" &&
                                    !issueName &&
                                    !linkToName))
                                  ? "#bfbfbf"
                                  : "#595959",
                              width: "100%",
                              justifyContent: "flex-start",
                              paddingLeft: "12px",
                              cursor:
                                openDrawer &&
                                ((linkType === "task" &&
                                  !taskName &&
                                  !linkToName) ||
                                  (linkType === "issue" &&
                                    !issueName &&
                                    !linkToName))
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            {openDrawer &&
                            ((linkType === "task" &&
                              !taskName &&
                              !linkToName) ||
                              (linkType === "issue" &&
                                !issueName &&
                                !linkToName))
                              ? `Please set ${linkType} name first`
                              : "Choose Files"}
                          </AntButton>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[24, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name={["documents", index, "remarks"]}
                        label={
                          <span style={{ fontWeight: 500, color: "#262626" }}>
                            Remarks
                          </span>
                        }
                      >
                        <TextArea
                          placeholder="Enter remarks (optional)"
                          rows={3}
                          style={{
                            borderRadius: "6px",
                            resize: "none",
                            border: "1px solid #d9d9d9",
                          }}
                          showCount
                          maxLength={500}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            })}

            {/* Add Another Document button - only for Task/Issue creation */}
            {openDrawer && (
              <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <Button
                  color="primary"
                  onClick={addDocument}
                  disabled={
                    openDrawer &&
                    ((linkType === "task" && !taskName && !linkToName) ||
                      (linkType === "issue" && !issueName && !linkToName))
                  }
                  style={{
                    height: "40px",
                    borderRadius: "6px",
                    width: "auto",
                    minWidth: "200px",
                  }}
                >
                  + Add Another Document
                </Button>
              </div>
            )}

            {/* Professional "Supported Files" section */}
            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#495057",
                  fontSize: "13px",
                }}
              >
                <InfoCircleOutlined
                  style={{ color: "#2557d6", fontSize: "14px" }}
                />
                <span>
                  <strong>Supported file types:</strong> PDF, DOC, DOCX, XLS,
                  XLSX, JPG, JPEG, PNG
                </span>
              </div>
            </div>
          </Form>
        </Drawer>
      )}
    </FormProvider>
  );
};

export default DocumentsTab;
